import useResizeObserver from "@react-hook/resize-observer";
import { RefObject, useLayoutEffect, useRef, useState } from "react";

import MoreVertIcon from "@material-ui/icons/MoreVert";
import { Popover } from "antd";
import styled from "styled-components";

import { ToolbarButton } from "components/base";

/**
 * The overflow menu the chart toolbar has, for the viewer strips.
 *
 * `ChartToolbar` collapses its own buttons this way (`useOverflowIndex` counts
 * what fits, `OverflowToggle` puts the rest behind a "..." popover), but both
 * of those read and write the multiphase-chart settings context, which no
 * viewer has. The mechanism is the same; the arithmetic is local, because a
 * viewer strip mixes buttons with a 1px rule where the chart's items are all
 * one size.
 */

/** Only ever a fallback: the real widths are measured. See `calibrate`. */
const ASSUMED_BUTTON_WIDTH = 30;
const ASSUMED_DIVIDER_WIDTH = 1;
const ASSUMED_GAP = 8;

const isDivider = (item: JSX.Element): boolean => String(item.key) === "divider";

interface Measurements {
  /** Rendered width per item key, taken from the DOM. */
  readonly widths: Record<string, number>;
  /** The strip's own computed column gap. */
  readonly gap: number;
  /** What the "..." trigger will take: it is one of these buttons. */
  readonly trigger: number;
}

const rowWidth = (widths: readonly number[], gap: number): number =>
  widths.reduce((sum, width) => sum + width, 0) + Math.max(0, widths.length - 1) * gap;

export interface VisToolbarFitArithmetic {
  /** Each item's rendered width, in the order the strip draws them. */
  readonly itemWidths: readonly number[];
  /** The strip's column gap, which every neighbouring pair pays. */
  readonly gap: number;
  /** What the "..." trigger takes, once anything has to go behind it. */
  readonly trigger: number;
  /** The room the host grants the strip. */
  readonly available: number;
}

/**
 * How many items fit, given what they measure.
 *
 * Split out from the hook and kept free of the DOM because it is the part that
 * has been wrong twice, and both times by under-counting: the gaps between
 * items, or the slot the "..." trigger takes at the end of the row, left out
 * of the sum. Either one lets one more button in than there is room for, and
 * the row runs past the host with nothing to catch it.
 */
export function visToolbarFitCount({
  itemWidths,
  gap,
  trigger,
  available
}: VisToolbarFitArithmetic): number {
  if (rowWidth(itemWidths, gap) <= available) return itemWidths.length;

  let fits = 0;
  for (let i = 0; i < itemWidths.length; i++) {
    // The trigger sits at the end of the row and takes a slot of its own.
    if (rowWidth([...itemWidths.slice(0, i + 1), trigger], gap) > available) break;
    fits = i + 1;
  }
  return fits;
}

export interface VisToolbarFitParams {
  /**
   * The element that GRANTS the width — the host's slot, which the strip is
   * portalled into. Never the strip itself: a flex row of fixed-width buttons
   * has a min-content width equal to their sum, so it does not shrink inside
   * its parent no matter what `flex` says. Measuring the strip therefore
   * always reports that everything fits, and the row is simply clipped.
   */
  readonly host: Maybe<HTMLElement>;
  /** The strip, read once to learn what its items really measure. */
  readonly stripRef: RefObject<HTMLElement>;
  readonly items: readonly JSX.Element[];
  /** Only a docked strip has a width worth fitting to. */
  readonly enabled: boolean;
}

/**
 * How many of `items` fit across the host, counting the room the "..." trigger
 * itself needs whenever anything has to go behind it.
 *
 * Item widths are MEASURED, on one calibration pass while everything is still
 * inline, rather than assumed from the button size in `ToolbarButton`'s style
 * map: an assumed pitch that is too small silently under-counts and the row
 * runs past its host again. After that pass the count depends only on the
 * host's width and those recorded widths — never on what is currently
 * rendered, which would shrink the row, re-measure and settle anywhere.
 */
export function useVisToolbarFitCount({
  host,
  stripRef,
  items,
  enabled
}: VisToolbarFitParams): number {
  const [available, setAvailable] = useState<Maybe<number>>(null);
  const measured = useRef<Maybe<Measurements>>(null);

  useResizeObserver(host ?? null, (entry) => {
    // A dashboard widget's strip floats over the plot at its natural width, so
    // it neither needs this nor should re-render for it.
    if (!enabled) return;
    setAvailable(entry.contentRect.width);
  });

  useLayoutEffect(() => {
    if (!enabled || measured.current) return;
    const strip = stripRef.current;
    // Only while every item is still inline, so children line up with items.
    if (!strip || strip.children.length !== items.length) return;
    const widths: Record<string, number> = {};
    let widest = 0;
    items.forEach((item, index) => {
      const child = strip.children[index] as HTMLElement;
      const width = child.getBoundingClientRect().width;
      if (!width) return;
      widths[String(item.key)] = width;
      if (!isDivider(item)) widest = Math.max(widest, width);
    });
    if (Object.keys(widths).length !== items.length) return;
    const gap = parseFloat(window.getComputedStyle(strip).columnGap);
    measured.current = {
      widths,
      gap: Number.isFinite(gap) ? gap : ASSUMED_GAP,
      trigger: widest || ASSUMED_BUTTON_WIDTH
    };
  }, [enabled, items.length, stripRef]);

  if (!enabled || available == null) return items.length;

  const fallback = (item: JSX.Element) =>
    isDivider(item) ? ASSUMED_DIVIDER_WIDTH : ASSUMED_BUTTON_WIDTH;
  const gap = measured.current?.gap ?? ASSUMED_GAP;
  const trigger = measured.current?.trigger ?? ASSUMED_BUTTON_WIDTH;
  const widthOf = (item: JSX.Element) =>
    measured.current?.widths[String(item.key)] ?? fallback(item);

  return visToolbarFitCount({
    itemWidths: items.map(widthOf),
    gap,
    trigger,
    available
  });
}

export interface VisToolbarOverflowProps {
  items: readonly JSX.Element[];
}

/** The "..." button, holding the buttons that did not fit. */
export function VisToolbarOverflow({ items }: VisToolbarOverflowProps): JSX.Element {
  const [open, setOpen] = useState(false);

  return (
    <Popover
      arrowPointAtCenter
      content={<OverflowWrapper>{items.map((item) => item)}</OverflowWrapper>}
      placement="bottomRight"
      trigger="click"
      open={open}
      onOpenChange={(next) => setOpen(next)}>
      <ToolbarButton
        active={false}
        icon={<MoreVertIcon fontSize="large" />}
        overflowLabel=""
        tooltipText="More"
        styleKey="xdaToolbar"
        onToggle={(next) => setOpen(next)}
        data-testid="vis-toolbar-overflow"
      />
    </Popover>
  );
}

// The chart's own overflow menu, styled the same way: a column of the same
// square buttons, icons only.
//
// Each of these toggles passes an `overflowLabel`, but `ToolbarButton` draws it
// in a second grid column outside a box that is a fixed width with
// `overflow: hidden`, so it is clipped and never seen. That is what the chart
// toolbar's menu looks like too, and it is deliberate here: do NOT lift the
// button's width to reveal those labels.
//
// The gap and the padding are the chart menu's, and so is the ROW: its buttons
// are `ToolbarButton`'s default 36px, while a viewer strip's are the 30px
// `xdaToolbar` size, so the same 4px gap gave a tighter pitch and the icons
// read as crowded. Fixing the row height at 36px puts the two menus on the
// same 40px rhythm without resizing the buttons themselves, which are shared
// with the strip.
const OverflowWrapper = styled.div`
  display: grid;
  gap: 4px;
  grid-auto-rows: 36px;
  align-items: center;
  max-height: 300px;
  border-radius: var(--border-radius);
  padding: 8px 12px;
  margin: -12px -16px;
  overflow: hidden auto;
`;
